if (!customElements.get('fullice-hero-slider')) {
  class FulliceHeroSlider extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.slides = [...this.querySelectorAll('.fullice-hero-slider__slide')];
      this.dots = [...this.querySelectorAll('[data-slide-dot]')];
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5000;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.swipeEnabled = this.dataset.enableSwipe === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hovered = false;
      this.touch = null;
      this.bindEvents();
      this.scheduleNextMedia();
      this.startTimer();
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.preheatTimer) window.clearTimeout(this.preheatTimer);
      this.preheatTimer = null;
      this.abortController?.abort();
      this.abortController = null;
    }

    bindEvents() {
      const signal = this.abortController.signal;
      this.dots.forEach((dot) => dot.addEventListener('click', () => {
        this.showSlide(Number(dot.dataset.slideDot), true);
      }, { signal }));

      this.addEventListener('mouseenter', () => {
        this.hovered = true;
        if (this.pauseOnHover) this.stopTimer();
      }, { signal });
      this.addEventListener('mouseleave', () => {
        this.hovered = false;
        this.startTimer();
      }, { signal });

      this.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
      this.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false, signal });
      this.addEventListener('touchend', () => this.onTouchEnd(), { passive: true, signal });
      this.addEventListener('touchcancel', () => { this.touch = null; }, { passive: true, signal });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.stopTimer();
        else this.startTimer();
      }, { signal });
      this.reducedMotion.addEventListener('change', () => this.startTimer(), { signal });

      document.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId || slide === event.target);
        if (index >= 0) this.showSlide(index, false);
      }, { signal });
    }

    startTimer() {
      this.stopTimer();
      if (!this.autoplay || this.slides.length < 2 || this.reducedMotion.matches || this.hovered || document.visibilityState === 'hidden') return;
      this.timer = window.setTimeout(() => this.showSlide(this.currentIndex + 1, true), this.autoplaySpeed);
    }

    stopTimer() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }

    async showSlide(requestedIndex, restartTimer) {
      if (this.slides.length < 2) return;
      const index = (requestedIndex + this.slides.length) % this.slides.length;
      if (index === this.currentIndex) {
        if (restartTimer) this.startTimer();
        return;
      }
      this.stopTimer();
      await this.activateMedia(this.slides[index]);
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      this.currentIndex = index;
      this.scheduleNextMedia();
      if (restartTimer) this.startTimer();
    }

    activateMedia(slide) {
      if (!slide || slide.dataset.mediaActivated === 'true') return Promise.resolve();
      const media = slide.querySelector('.fullice-hero-slider__media');
      const template = slide.querySelector('template.fullice-hero-slider__media-template');
      if (!media || !template) return Promise.resolve();
      const content = template.content.cloneNode(true);
      const image = content.querySelector('img');
      media.insertBefore(content, template);
      media.querySelector('.fullice-hero-slider__placeholder')?.remove();
      template.remove();
      slide.dataset.mediaActivated = 'true';
      if (!image) return Promise.resolve();
      if (image.complete && image.naturalWidth > 1) return Promise.resolve();
      return new Promise((resolve) => {
        const finish = () => resolve();
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        window.setTimeout(finish, 3000);
      });
    }

    scheduleNextMedia() {
      if (this.slides.length < 2) return;
      const activate = () => this.activateMedia(this.slides[(this.currentIndex + 1) % this.slides.length]);
      const schedule = () => {
        if (this.preheatTimer) window.clearTimeout(this.preheatTimer);
        this.preheatTimer = window.setTimeout(() => {
          this.preheatTimer = null;
          if ('requestIdleCallback' in window) window.requestIdleCallback(activate, { timeout: 1200 });
          else window.setTimeout(activate, 200);
        }, 1000);
      };
      if (document.readyState === 'complete') schedule();
      else window.addEventListener('load', schedule, { once: true, signal: this.abortController.signal });
    }

    onTouchStart(event) {
      if (!this.swipeEnabled || this.slides.length < 2 || event.touches.length !== 1) return;
      const touch = event.touches[0];
      this.touch = { startX: touch.clientX, startY: touch.clientY, deltaX: 0, deltaY: 0, horizontal: false };
      this.stopTimer();
    }

    onTouchMove(event) {
      if (!this.touch || event.touches.length !== 1) return;
      const touch = event.touches[0];
      this.touch.deltaX = touch.clientX - this.touch.startX;
      this.touch.deltaY = touch.clientY - this.touch.startY;
      const absX = Math.abs(this.touch.deltaX);
      const absY = Math.abs(this.touch.deltaY);
      if (absY > absX) return;
      if (absX > 10) {
        this.touch.horizontal = true;
        event.preventDefault();
      }
    }

    onTouchEnd() {
      if (!this.touch) return;
      const { deltaX, horizontal } = this.touch;
      this.touch = null;
      if (horizontal && Math.abs(deltaX) >= 45) this.showSlide(this.currentIndex + (deltaX < 0 ? 1 : -1), true);
      else this.startTimer();
    }
  }

  customElements.define('fullice-hero-slider', FulliceHeroSlider);
}

if (!window.fulliceHeroSliderEditorEventsBound) {
  window.fulliceHeroSliderEditorEventsBound = true;
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('fullice-hero-slider').forEach((slider) => slider.connectedCallback());
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('fullice-hero-slider').forEach((slider) => slider.disconnectedCallback());
  });
}
